import { useMemo, useState } from "react";
import { useSession, LogoutReason } from "../Auth/hooks/useSession";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import Modal from "../../components/modals/Modal";
import Button, { ButtonType } from "../../components/Button";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import UserInputs from "../../components/UserInputs";
import { Dropdown } from "primereact/dropdown";
import {
  CompanyUserRole,
  AssignableRoleOptionsBase,
  roleLabel
} from "../../utils/companyUserRole";
import { isNameValid, isEmailValid } from "../../utils/validation";

const EditCustomerUser = ({
  title,
  selectedUser,
  onCancel,
  setOpenEditor,
  onSaved,
  showModal,
  open,
  onRequestDelete,
  emailReadOnly,
  allowDelete,
  onRequestSave,
  roleLabelFn = roleLabel,
  roleOptions = AssignableRoleOptionsBase
}) => {
  const { reason } = useSession();

  // Ensure we only ever start with 4 (CustomerAdmin) or 6 (Learner).
  const initialRoleId = useMemo(() => {
    const allowed = [CompanyUserRole.CustomerAdmin, CompanyUserRole.Learner];
    const roleId = Number(selectedUser?.RoleId);
    if (allowed.includes(roleId)) {
      return roleId;
    } else {
      return CompanyUserRole.Learner;
    }
  }, [selectedUser]);

  // Initialise local form state in the shape expected by UserInputs/InputTextWithValidation
  const INITIAL_DATA = useMemo(() => ({
    firstName: { value: selectedUser?.FirstName ?? "", validation: 0 },
    lastName: { value: selectedUser?.LastName ?? "", validation: 0 },
    userEmail: { value: selectedUser?.Email ?? selectedUser?.Username ?? "", validation: 0 },
    accessLevel: { value: initialRoleId, validation: 0 }
  }), [selectedUser, initialRoleId]);

  const [inputValues, setInputValues] = useState(INITIAL_DATA);

  const deleteLabel = useMemo(() => {
    const fn = (inputValues.firstName?.value ?? selectedUser?.FirstName ?? "").trim();
    const ln = (inputValues.lastName?.value ?? selectedUser?.LastName ?? "").trim();
    const full = [fn, ln].filter(Boolean).join(" ");
    return full ? `Delete ${full}` : "Delete";
  }, [inputValues.firstName?.value, inputValues.lastName?.value, selectedUser]);

  function getNameValidation(value) {
    const raw = value ?? "";
    if (raw.trim().length === 0) {
      return 1;
    }
    if (!isNameValid(raw, false)) {
      return 1;
    }
    return 0;
  }

  function getEmailValidation(value) {
    const raw = value ?? "";
    if (raw.trim().length === 0) {
      return 1;
    }
    if (!isEmailValid(raw)) {
      return 1;
    }
    return 0;
  }

  function onChangeInput(fields) {
    setInputValues((prev) => {
      const next = { ...prev };

      if (fields.userFirstName) {
        const value = (fields.userFirstName.value !== undefined)
          ? fields.userFirstName.value
          : prev.firstName.value;

        next.firstName = {
          ...prev.firstName,
          ...fields.userFirstName,
          value: value,
          validation: getNameValidation(value)
        };
      }

      if (fields.userLastName) {
        const value = (fields.userLastName.value !== undefined)
          ? fields.userLastName.value
          : prev.lastName.value;

        next.lastName = {
          ...prev.lastName,
          ...fields.userLastName,
          value: value,
          validation: getNameValidation(value)
        };
      }

      if (!emailReadOnly && fields.userEmail) {
        const value = (fields.userEmail.value !== undefined)
          ? fields.userEmail.value
          : prev.userEmail.value;

        next.userEmail = {
          ...prev.userEmail,
          ...fields.userEmail,
          value: value,
          validation: getEmailValidation(value)
        };
      }

      return next;
    });
  }

  function onChangeAccessLevel(e) {
    const val = typeof e?.value === "number" ? e.value : Number(e?.value ?? 0);
    setInputValues((prev) => ({
      ...prev,
      accessLevel: { ...prev.accessLevel, value: val, validation: 0 }
    }));
  }

  function handleCancel() {
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    if (typeof setOpenEditor === "function") {
      setOpenEditor(false);
    }
  }

  function handleDelete() {
    if (typeof onRequestDelete === "function") {
      onRequestDelete(selectedUser);
      return;
    }

    if (typeof setOpenEditor === "function") {
      setOpenEditor(false);
    }
  }

  function onClickSaveChanges(enable) {
    const roleId = Number(inputValues.accessLevel.value);
    const updated = {
      ...selectedUser,
      FirstName: inputValues.firstName.value,
      LastName: inputValues.lastName.value,
      Username: inputValues.userEmail.value,
      RoleId: roleId,
      RoleName: roleLabelFn(roleId)
    };

    if (typeof onRequestSave === "function") {
      const isAdd = (selectedUser?.Id === null);
      onRequestSave(updated, { isAdd: isAdd, enable: enable });
      return;
    }

    if (typeof onSaved === "function") {
      onSaved(updated);
    }
    enable?.();
    if (typeof setOpenEditor === "function") {
      setOpenEditor(false);
    }
  }

  const isDirty = useMemo(() => {
    const initial = INITIAL_DATA;
    return (
      inputValues.firstName.value.trim() !== initial.firstName.value.trim() ||
      inputValues.lastName.value.trim() !== initial.lastName.value.trim() ||
      inputValues.userEmail.value.trim() !== initial.userEmail.value.trim() ||
      Number(inputValues.accessLevel.value) !== Number(initial.accessLevel.value)
    );
  }, [inputValues, INITIAL_DATA]);

  const hasRequiredValues =
    inputValues.firstName.value.trim().length > 0 &&
    inputValues.lastName.value.trim().length > 0 &&
    inputValues.userEmail.value.trim().length > 0;

  const hasValidNames =
    inputValues.firstName.validation === 0 &&
    inputValues.lastName.validation === 0;

  const hasValidEmail =
    emailReadOnly || inputValues.userEmail.validation === 0;

  const canSubmit =
    hasRequiredValues &&
    hasValidNames &&
    hasValidEmail &&
    isDirty;

  const content = (
    <div>
      <UserInputs
        nameLabel="Name"
        firstName={inputValues.firstName}
        lastName={inputValues.lastName}
        emailLabel="Email"
        email={inputValues.userEmail}
        onChangeInput={onChangeInput}
        emailReadOnly={emailReadOnly}
      />

      <div className="form-row mt-3">
        <label className="align-self-center" htmlFor="access-input">Access level</label>

        <div className="col-span-2">
          <Dropdown
            id="access-input"
            value={inputValues.accessLevel.value}
            options={roleOptions}
            onChange={onChangeAccessLevel}
            placeholder="Select access level"
            className={inputValues.accessLevel.validation === 1 ? "ap-invalid w-full" : "w-full"}
          />
          {inputValues.accessLevel.validation === 1 && (
            <>
              <div className="ap-invalid-icon">!</div>
              <div className="ap-invalid-feedback">Access level is not valid.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const buttons = (
    <>
      {allowDelete && (
        <Button type={ButtonType.Tertiary} label={deleteLabel} onClick={handleDelete} />
      )}
      <Button type={ButtonType.Tertiary} label={"Cancel"} onClick={handleCancel} />
      <Button
        type={ButtonType.Primary}
        disabled={!canSubmit}
        icon={faCircleCheck}
        label={"Save Changes"}
        onClick={(e, c) => onClickSaveChanges(c)}
        disableUntilClickCompleted={true}
      />
    </>
  );

  const EditModal = (
    <Modal
      title={title}
      showModal={showModal === true}
      content={content}
      size="m"
      modalButtons={buttons}
      close={handleCancel}
    />
  );

  const render = () => {
    if (reason !== LogoutReason.None) {
      return <AuthLogoutMessage reason={reason} />;
    }
    if (open) {
      return EditModal;
    }
    return <></>;
  };

  return <>{render()}</>;
};

export default EditCustomerUser;